import { ReactNode, ReactElement, useCallback, CSSProperties } from 'react';
import { UISpinner } from '../ui-spinner';
import styles from './ui-button.module.scss';
import { blue, charcoal, concrete, red } from '../../styles';
import { UIIcon, UIIconName } from '../ui-icon';
import { UITooltip } from '../ui-tooltip';

export type UIButtonVariant =
    | 'default'
    | 'textDefault'
    | 'primary'
    | 'textPrimary'
    | 'danger'
    | 'textDanger'
    | 'alternate';

export type UIButtonProps = {
    children?: ReactNode;
    type?: 'submit' | 'reset' | 'button' | undefined;
    size?: 'small' | 'normal';
    variant?: UIButtonVariant;
    disabled?: boolean;
    disabledTooltip?: string;
    loading?: boolean;
    onClick?: () => void;
    customHeight?: number;
    text?: string;
    leftIcon?: UIIconName;
    rightIcon?: UIIconName;
    withPadding?: boolean;
    /** Fills the container. A button shrink-wraps its content otherwise. */
    fullWidth?: boolean;
    customPadding?: number;
    withoutButtonHtmlTag?: boolean;
};
export function UIButton(props: UIButtonProps): ReactElement {
    const {
        children,
        type,
        size = 'normal',
        variant = 'default',
        disabled = false,
        disabledTooltip = '',
        loading,
        onClick,
        customHeight,
        text,
        leftIcon,
        rightIcon,
        withPadding = true,
        fullWidth = false,
        customPadding,
        withoutButtonHtmlTag = false
    } = props;

    // Variant has a corresponding classname
    const buttonClasses = [styles.button, styles[size], styles[variant]];
    if (fullWidth) {
        buttonClasses.push(styles.fullWidth);
    }
    let style: CSSProperties = {
        height: customHeight
    };

    if (customPadding) {
        style = {
            ...style,
            padding: customPadding
        };
    } else if (withPadding) {
        buttonClasses.push(styles[`${size}Padding`]);
    } else {
        buttonClasses.push(styles.noPadding);
    }

    const leftIconComponent = useCallback(() => {
        function getSize(): number {
            switch (size) {
                case 'small':
                    return 12;
                case 'normal':
                    return 16;
            }
        }

        if (leftIcon) {
            return (
                <UIIcon
                    name={leftIcon}
                    color={disabled ? concrete : getColor(variant)}
                    size={getSize()}
                />
            );
        } else {
            return undefined;
        }
    }, [leftIcon, size, variant, disabled]);

    const rightIconComponent = useCallback(() => {
        function getSize(): number {
            switch (size) {
                case 'small':
                    return 12;
                case 'normal':
                    return 16;
            }
        }

        if (rightIcon) {
            return (
                <UIIcon
                    name={rightIcon}
                    color={disabled ? concrete : getColor(variant)}
                    size={getSize()}
                />
            );
        } else {
            return undefined;
        }
    }, [rightIcon, size, variant, disabled]);

    const buttonContent = useCallback(() => {
        return (
            <>
                {leftIconComponent()}
                {text}
                {children}
                {rightIconComponent()}
                {loading && <UISpinner color={getColor(variant)} />}
            </>
        );
    }, [
        children,
        leftIconComponent,
        loading,
        rightIconComponent,
        text,
        variant
    ]);

    if (withoutButtonHtmlTag) {
        return (
            <div
                className={buttonClasses.join(' ')}
                onClick={onClick}
                style={style}
            >
                {buttonContent()}
            </div>
        );
    } else {
        if (disabled && disabledTooltip) {
            return (
                <UITooltip text={disabledTooltip} place="left">
                    <button
                        className={buttonClasses.join(' ')}
                        type={type}
                        disabled={true}
                        onClick={onClick}
                        style={style}
                    >
                        {buttonContent()}
                    </button>
                </UITooltip>
            );
        } else {
            return (
                <button
                    className={buttonClasses.join(' ')}
                    type={type}
                    disabled={disabled}
                    onClick={onClick}
                    style={style}
                >
                    {buttonContent()}
                </button>
            );
        }
    }
}

function getColor(variant: string): string {
    switch (variant) {
        case 'default':
        case 'textDefault':
            return charcoal;
        case 'primary':
        case 'danger':
            return 'white';
        case 'textPrimary':
            return blue;
        case 'textDanger':
            return red;
    }
    return charcoal;
}
